<script lang="ts">
	import { goto, preloadData } from '$app/navigation'
	import { navigating, page } from '$app/state'
	import { browser } from '$app/environment'
	import ExploreTripCard from '$lib/components/itineraries/ExploreTripCard.svelte'
	import PanelBody from '$lib/components/itineraries/PanelBody.svelte'
	import PanelHeader from '$lib/components/itineraries/PanelHeader.svelte'
	import blockState from '$lib/stores/block-state'
	import { track } from '$lib/utils/track'
	import { onMount, tick } from 'svelte'
	import { getContextClient, queryStore } from '@urql/svelte'
	import {
		ListingConnectionDocument,
		type ListingConnectionQuery,
		type ListingConnectionQueryVariables,
	} from '$lib/queries/generated/QueryListings'
	import ExplorePageLoading from '$components/itineraries/ExplorePageLoading.svelte'
	import InfiniteScrollObserver from '$components/display/InfiniteScrollObserver.svelte'
	import { collectNodes } from '$src/lib/utils/edges'
	import StickyHeader from '$components/display/StickyHeader.svelte'
	import FilterIcon from '$components/icons/FilterIcon.svelte'
	import modal from '$src/lib/stores/modal-state'
	import { ListingPaginationFilter } from '$src/lib/graphql/enums'

	const pageSize = 10
	// let activeTab = $derived(page.url.searchParams.get('tab') || 'free')

	const urql = getContextClient()
	let tripsStore = $derived(
		queryStore<ListingConnectionQuery, ListingConnectionQueryVariables>({
			client: urql,
			query: ListingConnectionDocument,
			variables: { first: pageSize, after: null, filter: ListingPaginationFilter.Paid },
			pause: !browser,
		}),
	)

	let nextIntentID = $state(null)

	// Use let for local component state
	let listings: any[] = $state([])
	let latestPageInfo: any = $state(null)
	let paginationFetching = $state(false)

	async function loadNextPage() {
		if (paginationFetching || !latestPageInfo?.hasNextPage) return

		try {
			paginationFetching = true
			// If cursor is null, fetch first page without 'after' parameter
			const variables: ListingConnectionQueryVariables = {
				first: pageSize,
				filter: ListingPaginationFilter.Paid,
			}

			// Only add after parameter if we have a valid cursor
			if (latestPageInfo.endCursor != null) {
				variables.after = latestPageInfo.endCursor
			}

			const result = await urql
				.query<ListingConnectionQuery, ListingConnectionQueryVariables>(
					ListingConnectionDocument,
					variables,
					{
						requestPolicy: 'cache-and-network',
					},
				)
				.toPromise()

			if (result.data?.listingConnection) {
				const newEdges = result.data.listingConnection.edges?.map((edge) => edge.node) ?? []

				// Always check for duplicates regardless of cursor
				const existingIds = new Set(listings.map((listing) => listing?.id))
				const uniqueNewEdges = newEdges.filter((listing) => !existingIds.has(listing?.id))

				// Always append unique edges to existing listings
				listings = [...listings, ...uniqueNewEdges]

				// Update page info for next pagination
				latestPageInfo = result.data.listingConnection.pageInfo
			}
		} catch (error) {
			console.error('Error loading next page:', error)
		} finally {
			// published = listings.filter((l) => l != null).sort((a, b) => (a?.rank ?? 0) - (b?.rank ?? 0))
			await tick()
			paginationFetching = false
		}
	}

	// Only handle initial data load in the effect
	$effect(() => {
		if ($tripsStore?.data?.listingConnection && listings.length === 0) {
			const newEdges = collectNodes($tripsStore.data.listingConnection)
			listings = newEdges
			latestPageInfo = $tripsStore.data.listingConnection.pageInfo
		}
	})

	let published = $derived(
		listings.filter((l) => l != null).sort((a, b) => (a?.rank ?? 0) - (b?.rank ?? 0)),
	)

	onMount(() => {
		if (blockState) {
			blockState.clearAllCustom()
			blockState.setMapState('trips')
		}
	})

	function buildTripURL(listingId: string) {
		return `/explore/${listingId}`
	}

	function handleClickTrip(listingId: string) {
		track('Explore Trip', { listingId: listingId })
		let nextURL = buildTripURL(listingId)

		setTimeout(() => {
			if (navigating) {
				nextIntentID = listingId
			}
		}, 100)

		goto(nextURL.toString())
	}

	function preloadListingPage(listingId: string) {
		let nextURL = buildTripURL(listingId)
		return preloadData(nextURL.toString())
	}

	function setActiveTab(tab: string) {
		goto(`/explore?tab=${tab}`, { keepFocus: true, replaceState: true })
	}
</script>

<PanelHeader>
	<h1 class="text-lg lg:text-2xl font-bold w-full">Explore Guides</h1>
</PanelHeader>

<PanelBody>
	<!-- <StickyHeader class="top-14 lg:top-12 w-full px-4">
		{#snippet titleSlot()}
			<div class="flex space-x-8 pb-2">
				<button
					onclick={() => setActiveTab('free')}
					class="linkUnderlineDark flex items-center justify-center pt-2 text-sm lg:text-base font-normal tracking-tight text-center {activeTab ===
					'free'
						? 'text-white font-medium'
						: 'text-brand-gray-3'}"
				>
					Free
				</button>
				<button
					onclick={() => setActiveTab('paid')}
					class="linkUnderlineDark flex items-center justify-center pt-2 text-sm lg:text-base font-normal tracking-tight text-center {activeTab ===
					'paid'
						? 'text-white font-medium'
						: 'text-brand-gray-3'}"
				>
					Paid
				</button>
			</div>
		{/snippet}

		<button
			aria-label="Filter"
			class="p-2 rounded-full hover:bg-brand-gray-4/20 transition-all transform hover:scale-105 touch-manipulation"
			onclick={() => modal.open('filter')}
		>
			<FilterIcon class="w-3 h-3 text-brand-gray-3" />
		</button>
	</StickyHeader> -->
	{#if $tripsStore?.fetching || $tripsStore?.data == null}
		<ExplorePageLoading />
	{:else}
		{#if published && published?.length > 0}
			<div class="w-full pt-4 pb-24 flex flex-col">
				{#each published as listing, index}
					{#if index !== 0}
						<div class="h-[1px] rounded-full my-6 bg-brand-gray-4/25">
							<!-- space -->
						</div>
					{/if}
					<ExploreTripCard
						trip={listing?.currentTrip}
						basePrice={listing?.basePrice}
						currency={listing?.currency}
						fetching={nextIntentID === listing?.currentTrip?.id}
						onclick={() => handleClickTrip(listing?.id)}
						onmouseenter={() => preloadListingPage(listing?.id)}
						weekendMinCost={listing?.currentTrip?.weekendCostRangeLower}
						weekendMaxCost={listing?.currentTrip?.weekendCostRangeUpper}
						weekMinCost={listing?.currentTrip?.weekCostRangeLower}
						weekMaxCost={listing?.currentTrip?.weekCostRangeUpper}
						monthMinCost={listing?.currentTrip?.monthCostRangeLower}
						monthMaxCost={listing?.currentTrip?.monthCostRangeUpper}
						categories={listing?.currentTrip?.categories}
						avgReviewRating={listing?.avgRating}
						numReviews={listing?.numReviews}
					/>
				{/each}
			</div>
		{/if}

		<InfiniteScrollObserver
			fetching={paginationFetching || $tripsStore?.fetching}
			hasNextPage={latestPageInfo?.hasNextPage}
			onenter={loadNextPage}
		/>
	{/if}
</PanelBody>
